import * as React from 'react';
import { ReactNode, RefObject } from 'react';
import { inject, observer } from 'mobx-react';
import { action } from 'mobx';
import { List, ListRowProps } from 'react-virtualized';
import { MdChevronLeft, MdChevronRight } from 'react-icons/md';
import {
  Menu,
  Item,
  Separator,
  useContextMenu,
  ItemParams,
} from 'react-contexify';
import _ from 'lodash';
import { FlexLayout } from '../FlexLayout';
import {
  ConfirmType,
  Global,
  GridLayoutHeader,
  GridLayoutProps,
} from '../../../constants';
import style from './GridLayout.module.scss';
import { Format, JoinClassName, Sha256 } from '../../../utils/string';
import { Confirm } from '../../../utils/confirm';
import 'react-contexify/dist/ReactContexify.css';
import { Sort } from '../../../utils/array';
import { GridRow, GridSumRow, paddingClassOf } from './GridRow';
import { PageComponent } from '../../../utils';

enum MENU_ACTION {
  SORT_ASC,
  SORT_DESC,
  LOCK,
  UNLOCK,
}

// 인스턴스마다 고유한 컨텍스트 메뉴 id — 한 페이지에 그리드가 여러 개면
// 같은 id의 Menu가 중복 마운트되어 이벤트가 겹친다
let gridContextMenuSeq = 0;

/**
 * 화면 밖으로 미리 그려 두는 행 수.
 *
 * 10 이면 보이는 행의 두 배 가까이를 늘 들고 있게 된다. 행이 싸진 뒤에는
 * 그만큼 미리 그릴 이유가 없다 — 구형 PC 에서는 그 자체가 비용이다.
 */
const OVERSCAN_ROWS = 4;

/** 열 구성이 그대로인가. 아이디 목록만 견준다. */
function sameColumns(a: Array<GridLayoutHeader>, b: Array<GridLayoutHeader>): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i].id !== b[i].id) return false;
  }
  return true;
}

interface GridLayoutState {
  isLoaded: boolean;
  focusedIndex: number;
  rowCount: number;
  railThumbWidth: number;
  isHorizontalMouseActive: boolean;
  isVisibleHorizontalScroll: boolean;
  isListVerticalScroll: boolean;
  colLock: number;
}

@inject('publicStore')
@observer
export class GridLayout extends PageComponent<
  GridLayoutProps,
  GridLayoutState
> {
  mainList: RefObject<List>;

  refLockList: RefObject<List>;

  data: any[];

  /**
   * 그룹 소계 행이 끼어들 때 표시 행 → 데이터 행 매핑 (그룹 미사용시 undefined)
   */
  rowMap?: Array<{ dataIndex: number; isSum: boolean }>;

  /**
   * 헤더 우클릭 정렬 상태. props.data가 갱신돼도 preRender에서 재적용해 정렬이 풀리지 않게 유지
   */
  sortKey?: string;

  sortDesc: boolean = false;

  triggeredOnBottom: boolean;

  group: Array<string>;

  isEnabledGroup: boolean;

  hasTrail: boolean;

  totalWidth: number;

  gridWidth: number;

  gridHeight: number;

  perPxWidth: number;

  scrollTop: number;

  virtualInnerHeight: number;

  littleWidth: number;

  prevFocusedIndex?: number;

  /**
   * 바깥이 다시 그린 횟수. 행 메모를 깨는 열쇠다.
   *
   * GridLayout 이 **제 상태**(포커스·스크롤)만 바꿀 때는 그대로 두고, 페이지가
   * 다시 그렸을 때만 올린다. 셀은 `render(item, scope, i)` 로 행 밖의 값도 읽어서,
   * 자료가 그대로여도 페이지가 움직였으면 다시 그려야 한다.
   */
  private epoch: number = 0;

  /** epoch 를 올릴지 가리는 기준 — props 객체는 부모가 다시 그릴 때만 새로 온다. */
  private lastProps?: GridLayoutProps;

  /** 폭 계산 캐시. 셀마다 매번 Math.round 를 돌리던 것을 열 목록이 바뀔 때만 돌린다. */
  private widthCache?: {
    headers: Array<GridLayoutHeader>;
    perPxWidth: number;
    littleWidth: number;
    widths: Array<number>;
    plain: Array<React.CSSProperties>;
    byColor: Map<string, Array<React.CSSProperties>>;
  };

  /** 보이는 열·잠금 열 목록 캐시. 행마다 새 배열을 만들면 행 메모가 매번 깨진다. */
  private headerCache?: {
    source: Array<GridLayoutHeader>;
    visible: Array<GridLayoutHeader>;
    colLock: number;
    fixed: Array<GridLayoutHeader>;
  };

  scrollHeader: RefObject<HTMLDivElement>;

  scrollTrail: RefObject<HTMLDivElement>;

  scrollBody: RefObject<HTMLDivElement>;

  /** 잠금 열 목록의 스크롤 컨테이너. 행 폭 CSS 변수를 여기에도 얹는다. */
  scrollFixedBody: RefObject<HTMLDivElement>;

  scrollHorizontalRail: RefObject<HTMLDivElement>;

  scrollHorizontalRailThumb: RefObject<HTMLDivElement>;

  /**
   * 하단 스크롤바 위에서 움직이는 녀석의 현재 위치
   */
  scrollHorizontalRailThumbX: number;

  /**
   * 하단 스크롤바의 최대 이동 범위
   */
  maxScrollHorizontalRailThumbX: number;

  /**
   * 하단 스크롤바가 1px 움직일때 본문의 X축이 얼만큼 움직일지
   */
  perPxScrollHorizontalRailThumbX1px: number;

  constructor(props: GridLayoutProps, context?: any) {
    super(props, context);
    this.mainList = React.createRef();
    this.refLockList = React.createRef();
    this.scrollHeader = React.createRef();
    this.scrollTrail = React.createRef();
    this.scrollBody = React.createRef();
    this.scrollFixedBody = React.createRef();
    this.scrollHorizontalRail = React.createRef();
    this.scrollHorizontalRailThumb = React.createRef();
    this.scrollTop = 0;
    this.littleWidth = 0;
    this.virtualInnerHeight = 0;
    this.totalWidth = 1;
    this.gridWidth = 1;
    this.gridHeight = 1;
    this.perPxWidth = 1; // 화면보다 내용물이 적을 경우 100% 맞추기 위해서
    this.triggeredOnBottom = false;
    this.group = props.header.filter((x) => x.group).map((x) => x.id);
    this.isEnabledGroup = this.group.length > 0;
    this.hasTrail = props.header.filter((x) => x.trail).length > 0;
    this.scrollHorizontalRailThumbX = 0;
    this.maxScrollHorizontalRailThumbX = 0;
    this.perPxScrollHorizontalRailThumbX1px = 0;
    this.data = props.data;

    this.state = {
      isLoaded: false,
      focusedIndex: 0,
      railThumbWidth: 100,
      rowCount: 0,
      isHorizontalMouseActive: false,
      isVisibleHorizontalScroll: false,
      isListVerticalScroll: false,
      colLock: 0,
    };
  }

  componentDidMount(): void {
    this.init();
  }

  componentDidUpdate(
    prevProps: Readonly<GridLayoutProps>,
    _prevState: Readonly<GridLayoutState>,
    _snapshot?: any,
  ): void {
    // 열이 바뀌었는지 보는 데 SHA-256 두 번은 비싸다. 726열짜리 화면이 있고
    // 이 비교는 **모든 갱신마다** 돈다 — 아이디를 그대로 견준다.
    if (!sameColumns(this.props.header, prevProps.header)) this.init();
    else if (this.props.data?.length !== prevProps.data?.length) {
      this.forceRepaint(
        !this.props.data?.length
        || !prevProps.data?.length
        || Sha256.make(JSON.stringify(this.props.data[0])) !== Sha256.make(JSON.stringify(prevProps.data[0])),
      );
    }
  }

  async forceRepaint(reset: boolean = false) {
    requestAnimationFrame(async () => {
      if (reset) {
        this.sortKey = undefined;
        await this.SS({ rowCount: 0 });
      }
      await this.preRender();
    });
  }

  async init() {
    this.setState({
      isLoaded: false,
    }, () => {
      setTimeout(() => {
        const { rowHeight } = this.props;
        const data = this.props.data || [];
        this.triggeredOnBottom = false;
        this.prevFocusedIndex = undefined;
        this.sortKey = undefined;
        this.group = this.props.header.filter((x) => x.group).map((x) => x.id);
        this.isEnabledGroup = this.group.length > 0;
        this.hasTrail = this.props.header.filter((x) => x.trail).length > 0;

        // horizontal scrollbar
        this.scrollHorizontalRailThumbX = 0;
        this.maxScrollHorizontalRailThumbX = 0;
        this.perPxScrollHorizontalRailThumbX1px = 0;
        const isListVerticalScroll = data.length * rowHeight > (this.scrollBody.current?.clientHeight || 0);

        let totalWidth = this.visibleHeaders.reduce(
          (p, c) => p + (c?.width || 100), 0,
        );

        let clientWidth = this.scrollHeader.current?.clientWidth || 0;
        const railWidth = this.scrollHorizontalRail.current?.clientWidth
          || Math.round(clientWidth / 3) - 40;
        let railThumbWidth = Math.round((railWidth * clientWidth) / totalWidth);
        let isVisibleHorizontalScroll = false;

        totalWidth = this.props.contentWidth ?? totalWidth;

        if (clientWidth - totalWidth > 0) {
          railThumbWidth = railWidth;
          this.maxScrollHorizontalRailThumbX = 0;
          this.perPxScrollHorizontalRailThumbX1px = 0;

          const tWidth = clientWidth + (isListVerticalScroll ? -12 : 0);
          this.perPxWidth = tWidth / totalWidth;
          this.littleWidth = tWidth - this.visibleHeaders.reduce(
            (p, c) => p + Math.round((c?.width || 100) * this.perPxWidth), 0,
          );
        } else {
          isVisibleHorizontalScroll = true;
          this.perPxWidth = 1;
          this.maxScrollHorizontalRailThumbX = railWidth - railThumbWidth;
          this.perPxScrollHorizontalRailThumbX1px = Math.ceil(
            (totalWidth + (isListVerticalScroll ? 12 : 0) - clientWidth) / this.maxScrollHorizontalRailThumbX,
          );
          if (isListVerticalScroll) clientWidth += 12;
        }
        //

        this.gridWidth = clientWidth;
        this.gridHeight = this.scrollBody.current?.clientHeight || 0;
        this.totalWidth = totalWidth;

        this.setState({
          isLoaded: true,
          railThumbWidth,
          isListVerticalScroll,
          isVisibleHorizontalScroll,
        }, () => {
          this.applyRowWidth(isVisibleHorizontalScroll);
          requestAnimationFrame(() => {
            this.virtualInnerHeight = (this.scrollBody.current?.clientHeight || 0);
            this.preRender();
          });
        });
      }, 10);
    });
  }

  @action
  private async preRender(): Promise<void> {
    return new Promise<void>((resolve) => {
      if (!this.state.isLoaded) {
        resolve();
        return;
      }

      const { header, rowHeight } = this.props;
      this.data = this.props.data || [];
      if (this.sortKey) {
        this.data = this.sortDesc
          ? Sort.desc(this.sortKey, this.data)
          : Sort.asc(this.sortKey, this.data);
      }
      this.gridHeight = this.scrollBody.current?.clientHeight || 0;
      this.hasTrail = this.props.header.filter((x) => x.trail).length > 0;

      const isListVerticalScroll = this.data.length * rowHeight > (this.scrollBody.current?.clientHeight || 0);
      if (this.state.isVisibleHorizontalScroll) {
        this.gridWidth = (this.scrollHeader.current?.clientWidth || 0) + (isListVerticalScroll ? 12 : 0);
      } else {
        const tWidth = (this.scrollHeader.current?.clientWidth || 0) + (isListVerticalScroll ? -12 : 0);
        this.perPxWidth = tWidth / this.totalWidth;
        this.littleWidth = tWidth - this.visibleHeaders.reduce(
          (p, c) => p + Math.round((c?.width || 100) * this.perPxWidth), 0,
        );
      }

      header.forEach((x) => {
        if (x.value) {
          this.data.forEach((y) => {
            // eslint-disable-next-line no-param-reassign
            y[x.id] = x.value!(y, this.props.scope);
          });
        }
      });

      // 행 ReactNode는 rowRenderer에서 보이는 행만 즉석 생성.
      // 그룹 사용시에만 소계 행 위치 계산용 인덱스 맵 생성 (숫자만 보관)
      if (this.isEnabledGroup) {
        const rowMap: Array<{ dataIndex: number; isSum: boolean }> = [];
        for (let i = 0; i < this.data.length; i += 1) {
          rowMap.push({ dataIndex: i, isSum: false });
          if (this.isGroupTail(this.data[i], i)) {
            rowMap.push({ dataIndex: i, isSum: true });
          }
        }
        this.rowMap = rowMap;
      } else {
        this.rowMap = undefined;
      }

      // 자료가 바뀌면 값 컬럼(`header.value`)이 행을 고쳐 쓴다. 행 참조는 그대로라
      // 메모가 그것을 못 본다 — 여기서 한 번 올려 보이는 행을 다시 그리게 한다.
      this.epoch += 1;

      this.setState({
        rowCount: this.rowMap ? this.rowMap.length : this.data.length,
        isListVerticalScroll,
        colLock: this.state?.colLock || this.props?.columnLockIndex || 0,
      }, async () => {
        this.applyRowWidth(this.state.isVisibleHorizontalScroll);
        await this.mainList.current?.forceUpdateGrid();
        await this.refLockList.current?.forceUpdateGrid();
        resolve();
      });
    });
  }

  /**
   * 행 폭을 CSS 변수로 두 목록에 얹는다.
   *
   * 가로 스크롤이 있으면 행은 전체 폭이어야 하고, 없으면 화면 폭에 맞춘다.
   * 예전에는 행마다 `this.state.isVisibleHorizontalScroll` 을 읽어 정했다.
   */
  private applyRowWidth(isVisibleHorizontalScroll: boolean): void {
    const width = isVisibleHorizontalScroll ? `${this.totalWidth}px` : '100%';
    this.scrollBody.current?.style.setProperty('--grid-row-width', width);
    this.scrollFixedBody.current?.style.setProperty('--grid-row-width', width);
  }

  private repaintRows(): void {
    this.mainList.current?.forceUpdateGrid();
    this.refLockList.current?.forceUpdateGrid();
  }

  private async onRowFocus(i: number, item: any, checkDeny: boolean): Promise<void> {
    if (this.state.focusedIndex === i) return;

    if (this.data[this.state.focusedIndex]?.new === '1') {
      if (checkDeny && this.props.denyWhenNewRow) {
        Confirm.show(
          '경고',
          '이 화면은 신규 입력시 행 이동을 지원하지 않습니다.',
          ConfirmType.INFO,
        );
        return;
      }
      const warning = await Confirm.show(
        '경고',
        '저장하지 않은 데이터는 사라집니다.\n다른 행으로 넘어가시겠습니까?',
        ConfirmType.QUESTION,
      );

      if (!warning) return;
    }

    this.prevFocusedIndex = this.state.focusedIndex;
    this.setState({ focusedIndex: i }, () => this.repaintRows());
    this.props.onRowClick && this.props.onRowClick(item, i);
  }

  /** 잠금 열 전체의 폭. 헤더와 본문 두 곳이 같은 값을 쓴다. */
  private fixedColumnsWidth(): number {
    const { widths } = this.cells(this.visibleHeaders);
    return this.fixedHeaders().reduce((a, _, y) => a + (widths[y] || 100) + 2, 0);
  }

  private fixedHeaders(): Array<GridLayoutHeader> {
    return this.headers().fixed;
  }

  private renderDataRow(i: number, isFixed: boolean): ReactNode {
    const item = this.data[i];
    const headers = isFixed ? this.fixedHeaders() : this.visibleHeaders;
    const calcBackgroundColor = this.props.rowColor ? this.props.rowColor(item) : '';
    const backgroundColor = calcBackgroundColor || (i % 2 === 0 ? '#fafafa' : '#f2f2f2');

    return (<GridRow
      item={item}
      index={i}
      headers={headers}
      cellStyles={this.cellStyles(headers, backgroundColor)}
      scope={this.props.scope}
      epoch={this.epoch}
      focused={this.state.focusedIndex === i}
      grouped={this.isGrouped(item, i)}
      onFocus={isFixed ? this.onFixedRowFocused : this.onRowFocused}
      onDoubleClick={this.onRowDoubleClicked}
    />);
  }

  /**
   * 행 클릭. 두 벌인 것은 **본문 쪽에서만** 미저장 행 확인 창을 띄우기 때문이다
   * (예전 `onRowFocus(..., !isFixed)`). 행마다 새 함수를 만들면 행 메모가 매번
   * 깨지므로 목록마다 하나씩 세워 두고 그대로 넘긴다.
   */
  private onRowFocused = (i: number) => {
    this.onRowFocus(i, this.data[i], true);
  };

  private onFixedRowFocused = (i: number) => {
    this.onRowFocus(i, this.data[i], false);
  };

  private onRowDoubleClicked = (i: number) => {
    this.props.onRowDoubleClick && this.props.onRowDoubleClick(this.data[i], i);
  };

  private renderSumRow(i: number, isFixed: boolean): ReactNode {
    const item = this.data[i];
    const headers = isFixed ? this.fixedHeaders() : this.visibleHeaders;

    return (<GridSumRow
      item={item}
      data={this.data}
      headers={headers}
      cellStyles={this.cells(headers).plain}
      scope={this.props.scope}
      epoch={this.epoch}
    />);
  }

  get visibleHeaders(): Array<GridLayoutHeader> {
    return this.headers().visible;
  }

  /**
   * 보이는 열과 잠금 열. **같은 배열을 계속 돌려준다.**
   *
   * 행마다 `filter`·`slice` 로 새 배열을 만들면 참조가 매번 달라져 행 메모가
   * 한 번도 안 걸린다. 열 목록이나 잠금 개수가 바뀔 때만 다시 만든다.
   */
  private headers(): { visible: Array<GridLayoutHeader>; fixed: Array<GridLayoutHeader> } {
    const source = this.props.header;
    const colLock = this.state?.colLock || 0;
    const cache = this.headerCache;

    if (cache && cache.source === source && cache.colLock === colLock) return cache;

    const visible = cache && cache.source === source
      ? cache.visible
      : source.filter((k) => !k.isHidden);
    const fixed = visible.slice(0, colLock).filter((x) => !x.isHidden);

    this.headerCache = {
      source, visible, colLock, fixed,
    };
    return this.headerCache;
  }

  /**
   * 셀 폭과 셀 스타일. 열 목록·배율이 그대로면 **같은 객체를 다시 쓴다.**
   *
   * 셀마다 `{width, maxWidth, minWidth, backgroundColor}` 를 새로 만들던 자리다.
   * 20열 30행이면 한 번 그릴 때마다 객체 600개였다.
   */
  private cells(headers: Array<GridLayoutHeader>): {
    widths: Array<number>;
    plain: Array<React.CSSProperties>;
    byColor: Map<string, Array<React.CSSProperties>>;
  } {
    const cache = this.widthCache;
    if (cache
      && cache.headers === headers
      && cache.perPxWidth === this.perPxWidth
      && cache.littleWidth === this.littleWidth) return cache;

    const widths = headers.map((x, y) => Math.round((x.width || 100) * this.perPxWidth)
      + (y === 0 ? this.littleWidth : -2));
    const plain = widths.map((width) => ({ width, maxWidth: width, minWidth: width }));

    this.widthCache = {
      headers,
      perPxWidth: this.perPxWidth,
      littleWidth: this.littleWidth,
      widths,
      plain,
      byColor: new Map(),
    };
    return this.widthCache;
  }

  /** 그 배경색의 셀 스타일 묶음. 색깔 수만큼만 만든다. */
  private cellStyles(headers: Array<GridLayoutHeader>, backgroundColor: string): Array<React.CSSProperties> {
    const cell = this.cells(headers);
    const found = cell.byColor.get(backgroundColor);
    if (found) return found;

    const made = cell.plain.map((base) => ({ ...base, backgroundColor }));
    cell.byColor.set(backgroundColor, made);
    return made;
  }

  public setFocus(index: number) {
    setTimeout(() => {
      this.prevFocusedIndex = this.state.focusedIndex;
      this.setState({ focusedIndex: index }, async () => {
        this.repaintRows();
        this.props.onRowClick && this.props.onRowClick(this.data[index], index);
        requestAnimationFrame(() => {
          this.mainList.current?.scrollToRow(index);
          this.refLockList.current?.scrollToRow(index);
        });
      });
    }, 500);
  }

  private isGrouped(item: any, i: number): boolean {
    let result = true;

    if (i === 0) {
      return false;
    }

    this.group.forEach((x) => {
      if (this.data[i - 1][x] !== item[x]) {
        result = false;
      }
    });

    return result;
  }

  private isGroupTail(item: any, i: number): boolean {
    let result = false;

    if (i === this.data.length - 1) {
      return true;
    }

    this.group.forEach((x) => {
      if (this.data[i + 1][x] !== item[x]) {
        result = true;
      }
    });

    return result;
  }

  private onHorizontalDragging(e: React.MouseEvent<HTMLDivElement>): void {
    if (e.buttons === 1 && !this.state.isHorizontalMouseActive) {
      this.setState({ isHorizontalMouseActive: true });
      e.preventDefault();
      e.stopPropagation();
    }
  }

  private onDeltaXEvent(value: number): void {
    if (value === 0) return;
    if (this.scrollHorizontalRailThumb.current) {
      let toThumbLeft = this.scrollHorizontalRailThumbX + value;
      if (toThumbLeft < 0) toThumbLeft = 0;
      if (toThumbLeft > this.maxScrollHorizontalRailThumbX) {
        toThumbLeft = this.maxScrollHorizontalRailThumbX;
      }

      this.scrollHorizontalRailThumbX = toThumbLeft;
      this.scrollHorizontalRailThumb.current.style.transform = `translateX(${toThumbLeft}px)`;

      const toBothScrollLeft = this.perPxScrollHorizontalRailThumbX1px * toThumbLeft;

      if (this.scrollHeader.current) this.scrollHeader.current.scrollLeft = toBothScrollLeft;
      if (this.scrollTrail.current) this.scrollTrail.current.scrollLeft = toBothScrollLeft;
      if (this.mainList.current) {
        this.mainList.current.Grid?.scrollToPosition({
          scrollLeft: toBothScrollLeft,
          scrollTop: this.scrollTop,
        });

        this.props.onHorizontalScroll && this.props.onHorizontalScroll(toBothScrollLeft);
      }
    }
  }

  private onMouseMove(e: React.MouseEvent<HTMLDivElement>): void {
    if (e.buttons === 1 && this.state.isHorizontalMouseActive) {
      this.onDeltaXEvent(e.movementX);
      e.preventDefault();
      e.stopPropagation();
    } else if (this.state.isHorizontalMouseActive) {
      requestAnimationFrame(() => {
        this.setState({ isHorizontalMouseActive: false });
      });
    }
  }

  private onMouseLeave(_: React.MouseEvent<HTMLDivElement>): void {
    if (!this.state.isHorizontalMouseActive) return;
    this.setState({ isHorizontalMouseActive: false });
  }

  private onWheel(e: React.WheelEvent<HTMLDivElement>): void {
    if (!this.state.isVisibleHorizontalScroll) return;
    this.onDeltaXEvent(e.deltaX);
  }

  private onKeyDown(e: React.KeyboardEvent<HTMLDivElement>): void {
    if (this.props.publicStore?.isLoading || this.props.publicStore?.isPreLoading) {
      e.preventDefault();
      return;
    }

    if (e.key === 'ArrowDown') {
      const current = this.state.focusedIndex;
      const next = current + 1;
      if (next < this.data.length) {
        this.prevFocusedIndex = this.state.focusedIndex;
        this.setState({ focusedIndex: next }, () => this.repaintRows());
        this.props.onRowClick && this.props.onRowClick(this.data[next], next);
        if (this.isVisibleRow(next)) e.preventDefault();
      }
    } else if (e.key === 'ArrowUp') {
      const current = this.state.focusedIndex;
      const next = current - 1;
      if (next > -1 && this.data.length > 0) {
        this.prevFocusedIndex = this.state.focusedIndex;
        this.setState({ focusedIndex: next }, () => this.repaintRows());
        this.props.onRowClick && this.props.onRowClick(this.data[next], next);
        if (this.isVisibleRow(next)) e.preventDefault();
      }
    }
  }

  private isVisibleRow(i: number): boolean {
    const visibleStart = Math.ceil(this.scrollTop / this.props.rowHeight);
    const visibleEnd = visibleStart + Math.floor(this.gridHeight / this.props.rowHeight);
    return i >= visibleStart && i <= visibleEnd;
  }

  private onListScroll = (e: any, isMain: boolean): void => {
    const { clientHeight, scrollTop, scrollHeight } = e;
    if (this.scrollTop === scrollTop) return;

    const top = Math.round(((clientHeight + scrollTop) / scrollHeight) * 100);
    this.scrollTop = scrollTop;

    if (isMain) {
      requestAnimationFrame(() => {
        this.refLockList.current?.scrollToPosition(scrollTop || 0);
      });
    } else {
      requestAnimationFrame(() => {
        this.mainList?.current?.scrollToPosition(scrollTop || 0);
      });
    }

    if (this.props.hasNext
      && !this.triggeredOnBottom
      && this.props.onBottom
      && top > 85
    ) {
      this.triggeredOnBottom = true;
      this.props.onBottom();
    }

    if (!this.props.onBottom
      && this.props.infinityHandler?.hasNext
      && top > 85
    ) {
      this.props.infinityHandler.retrieve();
    }
  }

  doScrollY = _.debounce(this.onListScroll, 300);

  menuId = `GRID_CONTEXT_${gridContextMenuSeq += 1}`;

  show = useContextMenu({
    id: this.menuId,
  }).show;

  handleContextMenu = (event: React.MouseEvent, item: any) => {
    this.show(event, {
      props: {
        scope: this,
        item,
      },
    });
  }

  @action
  onContextMenuHandler = ({ props }: ItemParams, action: MENU_ACTION) => {
    const { item, scope } = props;
    if (action === MENU_ACTION.SORT_ASC) {
      scope.sortBy(item.sort || item.id);
    } else if (action === MENU_ACTION.SORT_DESC) {
      scope.sortBy(item.sort || item.id, true);
    } else if (action === MENU_ACTION.LOCK) {
      let index = 0;
      for (let i = 0; i < scope.props.header.length; i += 1) {
        if (scope.props.header[i].id === item.id) {
          index = i;
          break;
        }
      }

      scope.setState({ colLock: index + 1 });
      this.forceRepaint(true);
    } else {
      scope.setState({ colLock: 0 });
      this.forceRepaint(true);
    }
  }

  @action
  async sortBy(key: string, isDesc: boolean = false) {
    if (!this.data.length) {
      return;
    }

    this.sortKey = key;
    this.sortDesc = isDesc;

    if (this.props.infinityHandler) {
      // onSort 미배선 페이지면 잔여분이 callback으로 공급됨 → props.data 갱신되면
      // componentDidUpdate → preRender에서 sortKey 재적용. 아래 preRender는 현재분 즉시 정렬.
      this.props.infinityHandler.sortBy(key, isDesc);
    }

    await this.preRender();
  }

  private rowRenderer = ({
    key,
    index: i,
    style: rowStyle,
  }: ListRowProps, isFixed: boolean = false) => {
    const entry = this.rowMap?.[i];
    const dataIndex = entry ? entry.dataIndex : i;
    const item = this.data?.[dataIndex];

    return (
      <div
        key={key}
        // 폭을 상태에서 읽지 않는다. 읽으면 가로 스크롤 유무가 뒤집힐 때마다
        // 보이는 행이 통째로 다시 그려진다 — 값은 CSS 변수로 컨테이너에 얹는다.
        style={{ ...rowStyle, width: 'var(--grid-row-width, 100%)' }}
      >
        {item !== undefined && (entry?.isSum
          ? this.renderSumRow(dataIndex, isFixed)
          : this.renderDataRow(dataIndex, isFixed))}
      </div>
    );
  }

  render() {
    // props 객체는 **부모가 다시 그릴 때만** 새로 온다. 그때는 셀이 읽는 화면 값도
    // 같이 바뀌었을 수 있으니 행 메모를 푼다. 포커스·스크롤처럼 우리 상태만
    // 바뀐 재렌더는 여기를 지나지 않아 행이 그대로 남는다.
    if (this.lastProps !== this.props) {
      this.lastProps = this.props;
      this.epoch += 1;
    }

    const {
      rowHeight,
      className,
      weight,
      minHeight,
      onClick,
    } = this.props;
    const { visibleHeaders } = this;
    const cellStyles = this.cells(visibleHeaders).plain;

    const innerLayout = (
      <React.Fragment>
        <FlexLayout
          elGrid={this.props.elGrid}
          weight={1}
          minHeight={minHeight}
          className={style.container}
        >
          <div
            ref={this.scrollBody}
            className={JoinClassName.make([style.scroller, style.body])}
          >
            <List
              ref={this.mainList}
              width={this.scrollHeader.current?.clientWidth || 0}
              onScroll={(e: any) => this.doScrollY(e, true)}
              overscanRowCount={OVERSCAN_ROWS}
              className={style.list}
              height={this.virtualInnerHeight}
              rowCount={this.state.rowCount}
              rowHeight={rowHeight}
              rowRenderer={this.rowRenderer}
            />
          </div>
        </FlexLayout>

        {this.hasTrail && <FlexLayout
          className={style.container}
          size={this.props.headerHeight || Global.LAYOUT_TITLE_HEIGHT_1}
          minHeight={this.props.headerHeight || Global.LAYOUT_TITLE_HEIGHT_1}
        >
          <div
            className={style.scroller}
          >
            <FlexLayout
              refs={this.scrollTrail}
              className={JoinClassName.make([
                style.container,
                style.footer,
              ])}
              weight={weight}
              size={this.props.rowHeight}
              minHeight={this.props.headerHeight || Global.LAYOUT_TITLE_HEIGHT_1}
              onClick={() => onClick && onClick()}
            >
              {visibleHeaders.map((item, y) => {
                const cell = item.trail && item.trail(this.data, this.props.scope);

                return (
                  <FlexLayout
                    key={item.id}
                    justify="center"
                    align="center"
                    className={JoinClassName.make([paddingClassOf(cell)])}
                    style={cellStyles[y]}
                  >
                    {cell}
                  </FlexLayout>
                );
              })}

              {this.state.isListVerticalScroll && <div style={{
                width: 10,
                flex: '0 0 10px',
              }} />}
            </FlexLayout>
          </div>
        </FlexLayout>}
      </React.Fragment>
    );

    const innerFixedColumnLayout = (
      <React.Fragment>
        <FlexLayout
          weight={1}
          minHeight={minHeight}
          className={style.container}
        >
          <div
            ref={this.scrollFixedBody}
            className={JoinClassName.make([style.scroller, style.body])}
          >
            <List
              ref={this.refLockList}
              width={this.scrollHeader.current?.clientWidth || 0}
              onScroll={(e: any) => this.doScrollY(e, false)}
              overscanRowCount={OVERSCAN_ROWS}
              className={style.list}
              height={this.virtualInnerHeight}
              rowCount={this.state.rowCount}
              rowHeight={rowHeight}
              rowRenderer={(x) => this.rowRenderer(x, true)}
            />
          </div>
        </FlexLayout>
      </React.Fragment>
    );

    return (
      <FlexLayout
        className={className}
        isVertical={true}
        onMouseMove={(e) => this.onMouseMove(e)}
        onMouseLeave={(e) => this.onMouseLeave(e)}
        onWheel={(e) => this.onWheel(e)}
        onKeyDown={(e) => this.onKeyDown(e)}
      >
        {/* Real header */}
        <FlexLayout
          className={style.container}
          weight={weight}
          size={this.props.headerHeight ? this.props.headerHeight : 32}
          minHeight={minHeight}
          onClick={() => onClick && onClick()}
        >
          <div
            ref={this.scrollHeader}
            className={style.scroller}
          >
            {this.state.isLoaded && <FlexLayout
              className={style.header}
              style={{
                width: this.perPxWidth === 1
                  ? this.totalWidth + (this.state.isListVerticalScroll ? 12 : 0)
                  : this.gridWidth,
                height: this.props.headerHeight ? this.props.headerHeight : 32,
              }}
            >
              {visibleHeaders.map((item, y) => (
                <div
                  key={y}
                  onContextMenu={(e) => this.handleContextMenu(e, item)}
                >
                  <FlexLayout
                    justify="center"
                    align="center"
                    style={{
                      ...cellStyles[y],
                      color: item.color || 'inherit',
                      height: this.props.headerHeight ? this.props.headerHeight : 32,
                    }}
                  >
                    {item.text}
                  </FlexLayout>
                </div>
              ))}

              {this.state.isListVerticalScroll && <div style={{
                width: 10,
                flex: '0 0 10px',
              }} />}
            </FlexLayout>}
          </div>
        </FlexLayout>

        {/* Fixed column header */}
        <FlexLayout
          className={JoinClassName.make([
            style.container,
            style.fixedCol,
          ])}
          weight={weight}
          size={this.props.headerHeight ? this.props.headerHeight : 32}
          minHeight={minHeight}
          onClick={() => onClick && onClick()}
          style={{
            width: this.fixedColumnsWidth(),
          }}
        >
          <div className={style.scroller}>
            {this.state.isLoaded && <FlexLayout
              className={style.header}
              style={{
                width: this.totalWidth < this.gridWidth
                  ? this.gridWidth
                  : this.totalWidth,
                height: this.props.headerHeight ? this.props.headerHeight : 32,
              }}
            >
              {visibleHeaders.map((item, y) => (
                <div
                  key={y}
                  onContextMenu={(e) => this.handleContextMenu(e, item)}
                >
                  <FlexLayout
                    justify="center"
                    align="center"
                    style={{
                      ...cellStyles[y],
                      color: item.color || 'inherit',
                      height: this.props.headerHeight ? this.props.headerHeight : 32,
                    }}
                  >
                    {item.text}
                  </FlexLayout>
                </div>
              ))}

              {this.state.isListVerticalScroll && <div style={{
                width: 10,
                flex: '0 0 10px',
              }} />}
            </FlexLayout>}
          </div>
        </FlexLayout>

        {/* Main */}
        {this.state.isLoaded && <FlexLayout isVertical={true}>
          {innerLayout}
        </FlexLayout>}

        {/* Fixed column main */}
        {this.state.isLoaded && <FlexLayout
          className={style.fixedColMain}
          isVertical={true}
          style={{
            top: (this.props.headerHeight || 32) + 2,
            bottom: (this.props.headerHeight ? (this.props.headerHeight + 22) : 56) + 22 + 2 - (this.hasTrail ? 22 : 0),
            width: this.fixedColumnsWidth(),
          }}
        >
          {innerFixedColumnLayout}
        </FlexLayout>}

        {this.state.isLoaded && <FlexLayout className={style.bottom} size={24}>
          <FlexLayout align="center" weight={2}>
            <FlexLayout style={{ color: '#818181' }}>
              <div>
                총 <span style={{ color: '#515151' }}>
                  {Format.number(this.props.infinityHandler?.total || this.data?.length || 0)}
                </span>행
              </div>
            </FlexLayout>
          </FlexLayout>

          {this.state.isVisibleHorizontalScroll && <FlexLayout
            align="center"
            justify="center"
          >
            <FlexLayout size={15}>
              <FlexLayout
                className={style.thumb}
                onClick={() => this.onDeltaXEvent(-50)}
              >
                <MdChevronLeft />
              </FlexLayout>
            </FlexLayout>
            <FlexLayout>
              <div
                ref={this.scrollHorizontalRail}
                className={style.rail}
              >
                <div
                  ref={this.scrollHorizontalRailThumb}
                  onMouseMove={(e) => this.onHorizontalDragging(e)}
                  style={{
                    width: this.state.railThumbWidth,
                  }}
                />
              </div>
            </FlexLayout>
            <FlexLayout size={15}>
              <FlexLayout
                className={style.thumb}
                onClick={() => this.onDeltaXEvent(50)}
              >
                <MdChevronRight />
              </FlexLayout>
            </FlexLayout>
          </FlexLayout>}
        </FlexLayout>}

        <Menu id={this.menuId}>
          <Item onClick={(p) => this.onContextMenuHandler(p, MENU_ACTION.SORT_ASC)}>
            오름차순 정렬
          </Item>
          <Item onClick={(p) => this.onContextMenuHandler(p, MENU_ACTION.SORT_DESC)}>
            내림차순 정렬
          </Item>
          <Separator />
          <Item onClick={(p) => this.onContextMenuHandler(p, MENU_ACTION.LOCK)}>
            틀 고정
          </Item>
          <Item onClick={(p) => this.onContextMenuHandler(p, MENU_ACTION.UNLOCK)}>
            틀 고정 해제
          </Item>
        </Menu>
      </FlexLayout>
    );
  }
}
